import { DialogActions, Button, Grid, TextField } from "@mui/material";
import { PropsWithChildren, Children, cloneElement } from "react";
import { useModalHooks } from "../../../State/Hooks/useModalHooks";

interface IModalFormProps {
  formik: any;
  isProcessing: boolean;
  children: any;
}

/**
 * @description Generates a formik form template for use in the Modal component
 * that includes cancel and submit button logic
 *
 * @param formik The formik instance needed for the child form
 * @param isProcessing Whether or not the form submission is processing
 * @param children The child form component
 * @returns An empty form with cancel and submit buttons
 */
export default function ModalForm({
  formik,
  isProcessing,
  children,
}: PropsWithChildren<IModalFormProps>) {
  const { closeModal } = useModalHooks();

  // Pass formik instance to children (children will be formik form inputs)
  const renderChildren = () => {
    return Children.map(children, (child) => {
      return cloneElement(child, {
        formik: formik,
      });
    });
  };

  // Clear the form on modal close to prevent unwanted side effects
  const handleModalClose = () => {
    if (!isProcessing) {
      closeModal();
      formik.resetForm();
    } else return;
  };

  return (
    <form onSubmit={formik.handleSubmit}>
      {renderChildren()}
      <DialogActions sx={{ pl: 0, pr: 0 }}>
        <Button
          disabled={isProcessing}
          sx={{ width: "50%" }}
          variant="outlined"
          onClick={() => handleModalClose()}
        >
          Cancel
        </Button>
        <Button
          disabled={isProcessing || !(formik.isValid && formik.dirty)}
          sx={{ width: "50%" }}
          variant="contained"
          type="submit"
        >
          Submit
        </Button>
      </DialogActions>
    </form>
  );
}
